import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { validateNpa, npaPlaceholder } from "@/lib/npa";
import { useNpaLookup } from "@/lib/useNpaLookup";

/**
 * Dialog réutilisable de création rapide d'un client.
 * Utilisé depuis le Calendrier, l'Atelier (OR), Devis, Facture.
 * iter122 — Champs obligatoires uniformisés : prénom+nom (ou raison_sociale) + tel + rue + n° + NPA + localité.
 * Accepte les deux noms de prop pour rétro-compat : initialQuery ET prefillSearch.
 * onCreated reçoit l'objet complet du client créé.
 */
export default function QuickClientDialog({ open, onClose, onCreated, initialQuery, prefillSearch, source = "quick_dialog" }) {
  const prefill = prefillSearch || initialQuery || "";
  const [type, setType] = useState("particulier");
  const [form, setForm] = useState({
    prenom: "", nom: "", raison_sociale: "",
    rue: "", numero: "", npa: "", localite: "",
    pays: "CH",
    tel_principal: "", email: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // iter164.5 — Auto-complétion NPA → Localité (openplzapi + geo.api.gouv.fr)
  const { options: localiteOptions, loading: localiteLoading } = useNpaLookup(
    form.npa, form.pays, form.localite,
    (name) => setForm((f) => ({ ...f, localite: name })),
  );

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!open) return;
    // Heuristique de pré-remplissage si la recherche contenait nom/prenom
    let pre = { prenom: "", nom: "", raison_sociale: "" };
    if (prefill) {
      const parts = prefill.trim().split(/\s+/);
      if (parts.length >= 2) { pre.prenom = parts[0]; pre.nom = parts.slice(1).join(" "); }
      else if (parts.length === 1) { pre.nom = parts[0]; }
    }
    setForm({
      ...pre,
      rue: "", numero: "", npa: "", localite: "",
      pays: "CH",
      tel_principal: "", email: "",
    });
    setType("particulier");
  }, [open, prefill]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const submit = async () => {
    // iter164.4 — Validation partagée via /lib/npa.js
    const check = validateNpa(form.npa, form.pays);
    if (!check.ok) {
      toast.error(check.message);
      return;
    }
    if (!form.rue || !form.numero || !form.localite || !form.tel_principal) {
      toast.error("Rue, n°, localité et téléphone requis");
      return;
    }
    if (type === "particulier" && (!form.prenom || !form.nom)) {
      toast.error("Prénom et nom requis");
      return;
    }
    if (type === "entreprise" && !form.raison_sociale) {
      toast.error("Raison sociale requise");
      return;
    }
    setSubmitting(true);
    try {
      const adresse = `${form.rue.trim()} ${form.numero.trim()}`.trim();
      const payload = {
        type,
        prenom: type === "particulier" ? form.prenom.trim() : null,
        nom: type === "particulier" ? form.nom.trim() : null,
        raison_sociale: type === "entreprise" ? form.raison_sociale.trim() : null,
        adresse,
        npa: form.npa.trim(),
        localite: form.localite.trim(),
        pays: (form.pays || "CH").trim().toUpperCase() || "CH",
        tel_principal: form.tel_principal.trim(),
        email: form.email.trim() || null,
        langue: "FR",
        statut: "actif",
        abonnement: "aucun",
        source,
      };
      const { data } = await api.post("/clients", payload);
      const nom = data.raison_sociale || `${data.prenom || ""} ${data.nom || ""}`.trim();
      toast.success(`Client « ${nom} » créé`);
      // Pour compat ascendante : on inclut id/nom/tel_principal/type/email + on remonte aussi tout l'objet
      onCreated({ id: data.id, nom, tel_principal: data.tel_principal, type: data.type, email: data.email, ...data });
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="quick-client-dialog">
        <DialogHeader>
          <DialogTitle>Créer un nouveau client</DialogTitle>
          <DialogDescription>
            Saisie rapide. Les autres champs (VIN, abonnement, etc.) pourront être complétés depuis la fiche client.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <button
              onClick={() => setType("particulier")}
              className={`flex-1 px-3 py-2 rounded-md border text-sm font-semibold ${type === "particulier" ? "bg-[#52a675] text-white border-[#52a675]" : "bg-white text-gray-700 border-gray-200"}`}
              data-testid="quick-type-particulier"
            >
              Particulier
            </button>
            <button
              onClick={() => setType("entreprise")}
              className={`flex-1 px-3 py-2 rounded-md border text-sm font-semibold ${type === "entreprise" ? "bg-[#52a675] text-white border-[#52a675]" : "bg-white text-gray-700 border-gray-200"}`}
              data-testid="quick-type-entreprise"
            >
              Entreprise
            </button>
          </div>

          {type === "particulier" ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Prénom *</Label>
                <Input value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} data-testid="quick-prenom" />
              </div>
              <div>
                <Label>Nom *</Label>
                <Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} data-testid="quick-nom" />
              </div>
            </div>
          ) : (
            <div>
              <Label>Raison sociale *</Label>
              <Input value={form.raison_sociale} onChange={(e) => setForm({ ...form, raison_sociale: e.target.value })} data-testid="quick-raison-sociale" />
            </div>
          )}

          <div className="grid grid-cols-4 gap-3">
            <div className="col-span-3">
              <Label>Rue *</Label>
              <Input value={form.rue} onChange={(e) => setForm({ ...form, rue: e.target.value })} placeholder="Avenue de la Gare" data-testid="quick-rue" style={{ fontSize: 16 }} />
            </div>
            <div>
              <Label>N° *</Label>
              <Input value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value })} placeholder="12" data-testid="quick-numero" style={{ fontSize: 16 }} />
            </div>
          </div>

          <div className="grid grid-cols-4 gap-3">
            <div>
              <Label>NPA *</Label>
              <Input
                value={form.npa}
                onChange={(e) => setForm({ ...form, npa: e.target.value.replace(/[^A-Za-z0-9 \-]/g, "").slice(0, 10) })}
                maxLength={10}
                placeholder={npaPlaceholder(form.pays)}
                data-testid="quick-npa"
              />
            </div>
            <div className="col-span-2">
              <Label className="flex items-center gap-1.5">
                Localité *
                {localiteLoading && <Loader2 className="w-3 h-3 animate-spin text-gray-400" />}
                {!localiteLoading && localiteOptions.length > 0 && (
                  <span className="text-[10px] text-emerald-600 font-normal" data-testid="quick-locality-hint">
                    {localiteOptions.length === 1 ? "auto-complété" : `${localiteOptions.length} choix`}
                  </span>
                )}
              </Label>
              <Input
                value={form.localite}
                onChange={(e) => setForm({ ...form, localite: e.target.value })}
                placeholder="Meyrin"
                list="quick-loc-options"
                data-testid="quick-localite"
              />
              <datalist id="quick-loc-options">
                {localiteOptions.map((opt, i) => (
                  <option key={i} value={opt.name}>{opt.canton ? `${opt.name} (${opt.canton})` : opt.name}</option>
                ))}
              </datalist>
            </div>
            <div>
              <Label>Pays *</Label>
              <select
                value={form.pays}
                onChange={(e) => setForm({ ...form, pays: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                data-testid="quick-pays"
              >
                <option value="CH">🇨🇭 CH</option>
                <option value="FR">🇫🇷 FR</option>
                <option value="DE">🇩🇪 DE</option>
                <option value="IT">🇮🇹 IT</option>
                <option value="AT">🇦🇹 AT</option>
                <option value="LI">🇱🇮 LI</option>
                <option value="">Autre</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Téléphone *</Label>
              <Input value={form.tel_principal} onChange={(e) => setForm({ ...form, tel_principal: e.target.value })} placeholder="+41 79 ..." data-testid="quick-tel" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="quick-email" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={submitting} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="quick-client-save">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Créer et sélectionner"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
